import React, { useState, useRef, useEffect } from 'react';
import { Place, ImageMetadata } from '../types';
import AuthenticatedLocationImage from './AuthenticatedLocationImage';

interface PlaceCardProps {
  place: Place;
  shopId: string;
  locationId: string;
  isSelected: boolean;
  onClick: () => void;
  onSlideDrop?: (slideId: string, placeId: string) => void;
  onEdit?: (place: Place) => void;
}

const PlaceCard: React.FC<PlaceCardProps> = ({
  place,
  shopId,
  locationId,
  isSelected,
  onClick,
  onSlideDrop,
  onEdit
}) => {
  // Image carousel state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageHovered, setImageHovered] = useState(false);
  const [prefetchTriggered, setPrefetchTriggered] = useState(false);
  const swipedRef = useRef(false);
  const touchStartRef = useRef<number | null>(null);
  const touchEndRef = useRef<number | null>(null);
  const imgDragStartRef = useRef<number | null>(null);
  const isImgDraggingRef = useRef(false);
  const minSwipeDistance = 30;

  const sortedImages: ImageMetadata[] = place.images || [];
  const hasImages = sortedImages.length > 0;
  const hasMultipleImages = sortedImages.length > 1;

  // Reset index if place changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [place.id]);

  const isTouchDevice = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;
  const showArrows = hasMultipleImages && imageHovered && !isTouchDevice;

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPrefetchTriggered(true);
    setCurrentImageIndex(prev => prev === 0 ? sortedImages.length - 1 : prev - 1);
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPrefetchTriggered(true);
    setCurrentImageIndex(prev => prev === sortedImages.length - 1 ? 0 : prev + 1);
  };

  const onImgTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    touchEndRef.current = null;
    touchStartRef.current = e.targetTouches[0].clientX;
  };

  const onImgTouchMove = (e: React.TouchEvent) => {
    touchEndRef.current = e.targetTouches[0].clientX;
  };

  const onImgTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (touchStartRef.current === null || touchEndRef.current === null || !hasMultipleImages) {
      touchStartRef.current = null;
      touchEndRef.current = null;
      return;
    }
    const distance = touchStartRef.current - touchEndRef.current;
    if (distance > minSwipeDistance) {
      setPrefetchTriggered(true);
      setCurrentImageIndex(prev => prev === sortedImages.length - 1 ? 0 : prev + 1);
      swipedRef.current = true;
    } else if (distance < -minSwipeDistance) {
      setPrefetchTriggered(true);
      setCurrentImageIndex(prev => prev === 0 ? sortedImages.length - 1 : prev - 1);
      swipedRef.current = true;
    }
    touchStartRef.current = null;
    touchEndRef.current = null;
  };

  const onImageMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    imgDragStartRef.current = e.clientX;
    isImgDraggingRef.current = false;
  };

  const onImageMouseMove = (e: React.MouseEvent) => {
    if (imgDragStartRef.current !== null && Math.abs(e.clientX - imgDragStartRef.current) > 5) {
      isImgDraggingRef.current = true;
    }
  };

  const onImageMouseUp = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (imgDragStartRef.current !== null && isImgDraggingRef.current && hasMultipleImages) {
      const distance = imgDragStartRef.current - e.clientX;
      if (distance > minSwipeDistance) {
        setPrefetchTriggered(true);
        setCurrentImageIndex(prev => prev === sortedImages.length - 1 ? 0 : prev + 1);
        swipedRef.current = true;
      } else if (distance < -minSwipeDistance) {
        setPrefetchTriggered(true);
        setCurrentImageIndex(prev => prev === 0 ? sortedImages.length - 1 : prev - 1);
        swipedRef.current = true;
      }
    }
    imgDragStartRef.current = null;
    isImgDraggingRef.current = false;
  };

  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = (e: React.DragEvent) => {
    // Store place and location info for the drop handler
    e.dataTransfer.setData('application/json', JSON.stringify({
      placeId: place.id,
      fromLocationId: locationId,
      placeName: place.name
    }));
    e.dataTransfer.effectAllowed = 'move';
    setIsDragging(true);

    // Auto-select on drag start
    onClick();
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if this is a slide being dragged
    try {
      const types = Array.from(e.dataTransfer.types);
      if (types.includes('application/json')) {
        e.dataTransfer.dropEffect = 'move';
        setIsDragOver(true);
      }
    } catch (error) {
      // Ignore errors
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));

      // Check if this is a slide drop (not a place drop)
      if (data.slideId && onSlideDrop) {
        onSlideDrop(data.slideId, place.id);
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  };

  return (
    <div
      onClick={onClick}
      draggable={true}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        backgroundColor: isDragOver ? 'var(--success-bg)' : ((isSelected || isDragging) ? 'var(--bg-card)' : 'var(--bg-primary)'),
        border: isDragOver ? '2px dashed var(--success)' : ((isSelected || isDragging) ? '2px solid var(--primary-light)' : '1px solid var(--border-light)'),
        borderRadius: '6px',
        cursor: 'grab',
        transition: 'all 0.15s ease',
        boxShadow: isDragOver
          ? '0 4px 12px rgba(76, 175, 80, 0.3)'
          : (isSelected
            ? '0 4px 6px rgba(205, 133, 63, 0.15)'
            : '0 1px 3px rgba(139, 69, 19, 0.05)'),
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = 'var(--bg-primary)';
          e.currentTarget.style.borderColor = 'var(--primary-light)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = 'var(--bg-primary)';
          e.currentTarget.style.borderColor = 'var(--border-light)';
        }
      }}
    >
      {/* Image carousel on top */}
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={() => {
          setImageHovered(true);
          if (!prefetchTriggered && hasMultipleImages) setPrefetchTriggered(true);
        }}
        onMouseLeave={() => setImageHovered(false)}
        onTouchStart={hasMultipleImages ? onImgTouchStart : undefined}
        onTouchMove={hasMultipleImages ? onImgTouchMove : undefined}
        onTouchEnd={hasMultipleImages ? onImgTouchEnd : undefined}
        onMouseDown={hasMultipleImages ? onImageMouseDown : undefined}
        onMouseMove={hasMultipleImages ? onImageMouseMove : undefined}
        onMouseUp={hasMultipleImages ? onImageMouseUp : undefined}
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '4px 4px 0 0',
          userSelect: 'none',
          WebkitUserSelect: 'none'
        }}
      >
        {hasImages ? (
          sortedImages.map((img, index) => {
            if (index !== currentImageIndex && !prefetchTriggered) return null;
            return (
              <AuthenticatedLocationImage
                key={img.id}
                shopId={shopId}
                locationId={locationId}
                placeId={place.id}
                imageId={img.id}
                alt={place.name}
                size="thumb_300"
                style={{
                  width: '100%',
                  aspectRatio: '1/1',
                  objectFit: 'cover',
                  pointerEvents: 'none',
                  ...(index !== currentImageIndex ? { position: 'absolute', visibility: 'hidden' } : {})
                }}
              />
            );
          })
        ) : (
          <div
            style={{
              width: '100%',
              aspectRatio: '1/1',
              backgroundColor: 'var(--bg-secondary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <img src="/logo-192.png" alt="Carousel Shop AI" style={{ width: '48px', height: '48px', opacity: 0.35, borderRadius: '8px' }} />
          </div>
        )}

        {/* Edit button overlaid on image */}
        {onEdit && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(place);
            }}
            style={{
              position: 'absolute',
              top: '6px',
              right: '6px',
              background: 'rgba(255, 255, 255, 0.85)',
              border: 'none',
              borderRadius: '50%',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'var(--primary-light)',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
              zIndex: 2
            }}
            title="Edit place"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.5 1.5L12.5 3.5L4.5 11.5H2.5V9.5L10.5 1.5Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9 3L11 5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}

        {/* Left arrow */}
        {hasMultipleImages && (
          <button
            onClick={handlePrevImage}
            style={{
              position: 'absolute',
              left: '4px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255, 255, 255, 0.8)',
              border: 'none',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '14px',
              color: 'var(--text-primary)',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
              opacity: showArrows ? 0.9 : 0,
              transition: 'opacity 0.2s ease',
              zIndex: 1,
              padding: 0,
              lineHeight: 1,
              WebkitTapHighlightColor: 'transparent'
            }}
            aria-label="Previous image"
          >
            &#8249;
          </button>
        )}

        {/* Right arrow */}
        {hasMultipleImages && (
          <button
            onClick={handleNextImage}
            style={{
              position: 'absolute',
              right: '4px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255, 255, 255, 0.8)',
              border: 'none',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '14px',
              color: 'var(--text-primary)',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
              opacity: showArrows ? 0.9 : 0,
              transition: 'opacity 0.2s ease',
              zIndex: 1,
              padding: 0,
              lineHeight: 1,
              WebkitTapHighlightColor: 'transparent'
            }}
            aria-label="Next image"
          >
            &#8250;
          </button>
        )}

        {/* Dot indicators */}
        {hasMultipleImages && (
          <div
            style={{
              position: 'absolute',
              bottom: '4px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 6px',
              borderRadius: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              zIndex: 1
            }}
          >
            {sortedImages.slice(0, 5).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: index === currentImageIndex ? 'var(--bg-card)' : 'rgba(255, 255, 255, 0.5)',
                  transition: 'background-color 0.2s ease'
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Content below image */}
      <div style={{ padding: '8px 10px 10px' }}>
        {/* Place Name */}
        <div
          style={{
            fontWeight: 600,
            fontSize: '0.875rem',
            color: 'var(--text-primary)',
            lineHeight: '1.3',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            maskImage: 'linear-gradient(to right, black 80%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to right, black 80%, transparent 100%)'
          }}
        >
          {place.name}
        </div>

        {/* Place Description */}
        {place.description && (
          <div
            style={{
              fontSize: '0.75rem',
              color: 'var(--text-secondary)',
              lineHeight: '1.4',
              marginTop: '2px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              maskImage: 'linear-gradient(to right, black 80%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to right, black 80%, transparent 100%)'
            }}
          >
            {place.description}
          </div>
        )}

        {/* Photo count */}
        {place.images && place.images.length > 0 && (
          <div
            style={{
              fontSize: '0.6875rem',
              color: 'var(--text-secondary)',
              marginTop: '4px'
            }}
          >
            {place.images.length} {place.images.length === 1 ? 'photo' : 'photos'}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlaceCard;
